import { Box } from "@mui/material";
import { useUser } from "@/app/providers";
import React, { useEffect, useMemo } from "react";
import { TenantDropdownAutoSave } from "@/features";
import { useTenantList, useUpdateProfile } from "@/hooks";
import { object } from "prop-types";
import { getRoleTypeNameFromUser } from "@/utils";
import type { SxProps, Theme } from "@mui/material/styles";
import type { TenantUserType } from "@/types/graphql";

type TenantSelectorProps = {
  sx?: SxProps<Theme>;
};

/**
 * Tenant/company selector that derives the available companies for the current user based on permissions,
 * auto-initializes the current team when missing, and persists changes to the user profile.
 * - Uses text domain and user contexts plus data hooks to load either all customers or the user's companies.
 * - If no current team exists, selects the first available company and updates profile state/server.
 * - Renders a dropdown when multiple companies are available; otherwise shows the current team or a translated fallback.
 * @param {object} props - Component props.
 * @param {object} [props.sx] - MUI sx overrides applied to the dropdown container.
 * @returns {React.JSX.Element} Tenant selector UI.
 */
export const TenantSelector = (props: TenantSelectorProps) => {
  const { user, setUser } = useUser();
  const { id: userId, currentTenantId, currentRoleKey } = user || {};
  const { tenantsList } = (useTenantList as any)();

  const { updateProfile } = useUpdateProfile();

  const roleType = getRoleTypeNameFromUser(user);

  // const [tenants, setTenants] = useState([]);

  const boxSx = useMemo(() => {
    return {
      ...props.sx,
      ml: 2,
    };
  }, [props.sx]);

  useEffect(() => {
    if (!currentTenantId) {
      switch (roleType) {
        case "company":
        case "partner": {
          let variables;
          if (tenantsList.length) {
            variables = {
              currentTenantId: tenantsList[0]?.id || tenantsList[0]?.Id,
            };
          } else {
            variables = {
              currentTenantId: "none",
            };
          }
          updateProfile(variables);
          setUser((prev: any) => ({
            ...prev,
            ...variables,
          }));
          break;
        }
        case "system":
        case "super": {
          let variables;
          if (tenantsList.length) {
            variables = {
              currentTenantId: tenantsList[0]?.id || tenantsList[0]?.Id,
            };
            updateProfile(variables);
          } else {
            variables = {
              currentTenantId: "",
            };
          }
          setUser((prev: any) => ({
            ...prev,
            ...variables,
          }));
          break;
          // Do not update it in users collection. "all" is not a tenant ID
        }
        default:
          // Do nothing. technically that part never hit.
          break;
      }
    }
  }, [currentTenantId, roleType, setUser, tenantsList, updateProfile]);

  useEffect(() => {
    if (!currentRoleKey) {
      if (currentTenantId) {
        if (tenantsList.length > 0) {
          const tenantUser = tenantsList[0]?.users.find(
            (user: TenantUserType | null) => user?.userId === userId,
          );
          if (tenantUser?.role) {
            const variables = {
              currentRoleKey: tenantUser?.role,
            };
            updateProfile(variables);
            setUser((prev: any) => ({
              ...prev,
              ...variables,
            }));
          }
        }
      }
    }
  }, [
    currentRoleKey,
    currentTenantId,
    setUser,
    tenantsList,
    updateProfile,
    userId,
  ]);

  return (
    <Box sx={boxSx}>{tenantsList.length > 0 && <TenantDropdownAutoSave />}</Box>
  );
};

TenantSelector.propTypes = {
  sx: object,
};
